import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { ResumeIndicatorM1ByHealthTeamData } from "@/services/esus/indicator/emulti/m1/getResumeIndicatorM1ByHealthTeam";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getIndicatorClassification, IndicatorEmultiEnum } from "@/utils/indicatorsEmulti";
import { calculatePercentage } from "@/utils/calculatePercentage";

import { IndicatorEmultiM1HealthTeamsFilters } from "./IndicatorEmultiM1HealthTeamsFilters";
import { IndicatorEmultiM1HealthTeamsClassificationTable } from "./IndicatorEmultiM1HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEmultiM1HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorM1ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorM1ByHealthTeam: () => Promise<ResumeIndicatorM1ByHealthTeamData[]>;
};

export function IndicatorEmultiM1HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorM1ByHealthTeam,
}: Readonly<IndicatorEmultiM1HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorM1ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorM1AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Procedimentos por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorM1AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorM1ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					Equipe: healthTeamClassification.healthTeamName,
					"Equipe INE": healthTeamClassification.healthTeamIne,
					Atendimentos: healthTeamClassification.totalAttendances,
					Cidadãos: healthTeamClassification.totalCitizens,
					"Média de atendimentos por cidadão": `${calculatePercentage(
						healthTeamClassification.totalAttendances / 100,
						healthTeamClassification.totalCitizens
					)}`,
					Classificação: getIndicatorClassification(
						IndicatorEmultiEnum.M1,
						calculatePercentage(
							healthTeamClassification.totalAttendances / 100,
							healthTeamClassification.totalCitizens
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEmultiM1HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEmultiM1HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
